/**
 * Small React utilities shared by the feature folders.
 *
 * `useDebouncedCallback` is the one §12.3/§12.4 explicitly asks for: every UI-state write-through
 * (view filters, column layout) is debounced 500 ms and flushed on unmount so a change made just
 * before navigating away is not lost.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Latest-value ref — lets a stable callback read fresh props without re-subscribing. */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export type DebouncedCallback<A extends unknown[]> = ((...args: A) => void) & {
  /** Run the pending call immediately (used on unmount and before navigation). */
  flush: () => void;
  cancel: () => void;
};

export function useDebouncedCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  delayMs: number,
): DebouncedCallback<A> {
  const latest = useLatest(callback);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<A | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    pending.current = null;
  }, []);

  const flush = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const args = pending.current;
    pending.current = null;
    if (args !== null) latest.current(...args);
  }, [latest]);

  const debounced = useCallback(
    (...args: A) => {
      pending.current = args;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        const queued = pending.current;
        pending.current = null;
        if (queued !== null) latest.current(...queued);
      }, delayMs);
    },
    [delayMs, latest],
  ) as DebouncedCallback<A>;

  debounced.flush = flush;
  debounced.cancel = cancel;

  const flushRef = useLatest(flush);
  useEffect(() => () => flushRef.current(), [flushRef]);

  return debounced;
}

/**
 * Closes popovers/menus on an outside pointer press or Escape.
 *
 * `extraRefs` covers panels that live outside the returned ref's subtree — a portaled overlay
 * (§12) is not a DOM descendant of its trigger, so without it every click *inside* the panel
 * counts as an outside press and dismisses it.
 */
export function useDismissable<T extends HTMLElement>(
  open: boolean,
  onDismiss: () => void,
  extraRefs?: readonly RefObject<HTMLElement | null>[],
): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const latest = useLatest(onDismiss);
  const extras = useLatest(extraRefs);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      const target = event.target;
      const inside = (node: HTMLElement | null) => node !== null && node.contains(target);
      if (inside(ref.current)) return;
      if ((extras.current ?? []).some((extra) => inside(extra.current))) return;
      latest.current();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') latest.current();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, latest, extras]);

  return ref;
}

/**
 * Fires once when the element scrolls into view — the lazy-mount trigger for media (§9.3) and the
 * infinite-scroll sentinel of the feed.
 */
export function useInView<T extends Element>(
  enabled: boolean,
  onEnter: () => void,
  options?: IntersectionObserverInit,
): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const latest = useLatest(onEnter);
  const rootMargin = options?.rootMargin;
  const threshold = options?.threshold;

  useEffect(() => {
    const node = ref.current;
    if (!enabled || node === null) return;
    if (typeof IntersectionObserver === 'undefined') {
      latest.current();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) latest.current();
      },
      { rootMargin, threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, latest, rootMargin, threshold]);

  return ref;
}

/**
 * Names the browser tab after the current screen.
 *
 * **Set-only: deliberately no restore on unmount.** Two screens can be mounted at the same moment
 * (a `<Navigate>` frame, a router transition, `StrictMode`'s double-invoke). If the incoming
 * screen's effect runs before the outgoing screen's cleanup, a restoring cleanup writes the *wrong*
 * title back and it sticks until the next navigation. Set-only has one failure mode instead — a
 * screen that sets no title inherits the previous one — and every screen that has a name sets it.
 */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/**
 * Tracks a CSS media query.
 *
 * Layout that only *looks* different belongs in CSS; this is for the case where the two states are
 * different components — the feed's sidebar is an island above 1100px and a drawer below it, and a
 * drawer that is merely `display: none` still traps focus and still answers Escape.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Copy-to-clipboard with the brief "Copied" confirmation the PRD asks for. */
export function useCopyToClipboard(resetMs = 1600): [boolean, (text: string) => void] {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), resetMs);
    return () => clearTimeout(timer);
  }, [copied, resetMs]);

  const copy = useCallback((text: string) => {
    const done = () => setCopied(true);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => setCopied(false));
      return;
    }
    // Fallback for non-secure contexts, where the async clipboard API is unavailable.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand('copy');
      done();
    } catch {
      setCopied(false);
    } finally {
      document.body.removeChild(area);
    }
  }, []);

  return [copied, copy];
}
